import clsx from 'clsx';
import styles from './Stepper.module.css';

/**
 * Step component for use within Stepper.
 *
 * @param {Object} props
 * @param {number} props.number - Step number (1-indexed)
 * @param {string} props.label - Step label text
 * @param {boolean} [props.isActive] - Whether this step is currently active
 * @param {boolean} [props.isCompleted] - Whether this step is completed
 */
function Step({ number, label, isActive = false, isCompleted = false }) {
  return (
    <div
      className={clsx(
        styles.step,
        isActive && styles.active,
        isCompleted && styles.completed
      )}
      aria-current={isActive ? 'step' : undefined}
    >
      <div className={styles.stepIndicator}>
        {isCompleted ? (
          <svg
            className={styles.checkIcon}
            viewBox="0 0 20 20"
            fill="currentColor"
            aria-hidden="true"
          >
            <path
              fillRule="evenodd"
              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
              clipRule="evenodd"
            />
          </svg>
        ) : (
          <span className={styles.stepNumber}>{number}</span>
        )}
      </div>
      <span className={styles.stepLabel}>{label}</span>
    </div>
  );
}

/**
 * Stepper component for multi-step wizards.
 *
 * Uses compound component pattern - pass Step components as children.
 *
 * @param {Object} props
 * @param {number} props.currentStep - Current active step (1-indexed)
 * @param {React.ReactNode} props.children - Step components
 * @param {string} [props.className] - Additional CSS classes
 *
 * @example
 * <Stepper currentStep={2}>
 *   <Stepper.Step number={1} label="Match Details" />
 *   <Stepper.Step number={2} label="FTP Destinations" />
 *   <Stepper.Step number={3} label="IPTC Config" />
 * </Stepper>
 */
function Stepper({ currentStep, children, className }) {
  return (
    <nav className={clsx(styles.stepper, className)} aria-label="Progress">
      <ol className={styles.stepList}>
        {children.map((child, index) => {
          const stepNumber = child.props.number || index + 1;
          const isActive = stepNumber === currentStep;
          const isCompleted = stepNumber < currentStep;

          return (
            <li key={stepNumber} className={styles.stepItem}>
              <Step
                {...child.props}
                number={stepNumber}
                isActive={isActive}
                isCompleted={isCompleted}
              />
              {index < children.length - 1 && (
                <div
                  className={clsx(
                    styles.connector,
                    isCompleted && styles.connectorCompleted
                  )}
                  aria-hidden="true"
                />
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

// Attach Step as static property for compound component pattern
Stepper.Step = Step;

export default Stepper;
